import React, { useState, useEffect, useCallback } from 'react';
import { Table, Card, Input, Button, Form, Select, DatePicker, message, InputNumber } from 'antd';
import apiClient from '../utils/apiClient';
import { useAuth } from '../contexts/AuthContext';

const { RangePicker } = DatePicker;
const { Option } = Select;

const TapitaCredits = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState({
    credits: false,
    history: false,
  });
  const [credits, setCredits] = useState([]);
  const [history, setHistory] = useState([]);
  const [pagination, setPagination] = useState({
    credits: {
      current: 1,
      pageSize: 10,
      total: 0,
    },
    history: {
      current: 1,
      pageSize: 20,
      total: 0,
    },
  });
  const [filters, setFilters] = useState({
    credits: {
      myshopify_domain: '',
    },
    history: {
      myshopify_domain: '',
      type: '',
      dateRange: null,
    },
  });
  const [form] = Form.useForm();

  const fetchCredits = useCallback(
    async (params = {}) => {
      setLoading((prev) => ({ ...prev, credits: true }));
      try {
        const { page = 1 } = params;
        let query = `page=${page}`;

        if (filters.credits.myshopify_domain) {
          query += `&myshopify_domain=${filters.credits.myshopify_domain}`;
        }

        const response = await apiClient.get(`/tapita-credits/admin/credits?${query}`);

        setCredits(response.data.credits);
        setPagination((prev) => ({
          ...prev,
          credits: {
            ...prev.credits,
            current: page,
            total: response.data.total,
          },
        }));
      } catch (error) {
        message.error('Failed to fetch credits');
      } finally {
        setLoading((prev) => ({ ...prev, credits: false }));
      }
    },
    [filters.credits.myshopify_domain]
  );

  const fetchHistory = useCallback(
    async (params = {}) => {
      setLoading((prev) => ({ ...prev, history: true }));
      try {
        const { page = 1 } = params;
        let query = `page=${page}`;

        if (filters.history.myshopify_domain) {
          query += `&myshopify_domain=${filters.history.myshopify_domain}`;
        }
        if (filters.history.type) {
          query += `&type=${filters.history.type}`;
        }
        if (filters.history.dateRange) {
          query += `&startDate=${filters.history.dateRange[0].toISOString()}&endDate=${filters.history.dateRange[1].toISOString()}`;
        }

        const response = await apiClient.get(`/tapita-credits/admin/history?${query}`);

        setHistory(response.data.history);
        setPagination((prev) => ({
          ...prev,
          history: {
            ...prev.history,
            current: page,
            total: response.data.total,
          },
        }));
      } catch (error) {
        message.error('Failed to fetch credit history');
      } finally {
        setLoading((prev) => ({ ...prev, history: false }));
      }
    },
    [filters.history.myshopify_domain, filters.history.type, filters.history.dateRange]
  );

  const handleCreditsTableChange = (pagination) => {
    fetchCredits({ page: pagination.current });
  };

  const handleHistoryTableChange = (pagination) => {
    fetchHistory({ page: pagination.current });
  };

  const handleCreditsSearch = () => {
    setPagination((prev) => ({
      ...prev,
      credits: { ...prev.credits, current: 1 },
    }));
    fetchCredits({ page: 1 });
  };

  const handleHistorySearch = () => {
    setPagination((prev) => ({
      ...prev,
      history: { ...prev.history, current: 1 },
    }));
    fetchHistory({ page: 1 });
  };

  const handleUpdateCredit = async (values) => {
    try {
      const response = await apiClient.post('/tapita-credits/admin/update-credit', {
        ...values,
        message: `Manual ${values.type} by ${user.username}`,
      });

      message.success(response.data.message);
      form.resetFields();
      // Refresh both tables
      fetchCredits({ page: 1 });
      fetchHistory({ page: 1 });
    } catch (error) {
      message.error('Failed to update credit');
    }
  };

  useEffect(() => {
    fetchCredits();
    fetchHistory();
  }, [fetchCredits, fetchHistory]);

  const creditColumns = [
    {
      title: 'Shop',
      dataIndex: 'myshopify_domain',
      key: 'myshopify_domain',
    },
    {
      title: 'Current Credit',
      dataIndex: 'credit',
      key: 'credit',
      sorter: (a, b) => a.credit - b.credit,
    },
    {
      title: 'Last Updated',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      render: (date) => new Date(date).toLocaleString(),
    },
  ];

  const historyColumns = [
    {
      title: 'Shop',
      dataIndex: 'myshopify_domain',
      key: 'myshopify_domain',
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      render: (type) => <span style={{ color: type === 'plus' ? 'green' : 'red' }}>{type === 'plus' ? '+' : '-'}</span>,
    },
    {
      title: 'Value',
      dataIndex: 'changing_value',
      key: 'changing_value',
    },
    {
      title: 'App',
      dataIndex: 'app',
      key: 'app',
    },
    {
      title: 'Admin',
      dataIndex: 'adminUser',
      key: 'adminUser',
      render: (adminUser) => adminUser || '-',
    },
    {
      title: 'IP',
      dataIndex: 'ip',
      key: 'ip',
    },
    {
      title: 'Message',
      dataIndex: 'message',
      key: 'message',
    },
    {
      title: 'Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => new Date(date).toLocaleString(),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Card title="Update Tapita Credits" style={{ marginBottom: '24px' }}>
        <Form form={form} onFinish={handleUpdateCredit} layout="inline">
          <Form.Item
            name="myshopify_domain"
            rules={[
              {
                required: true,
                message: 'Shop domain is required',
                pattern: /^[a-zA-Z0-9][a-zA-Z0-9-]*\.myshopify\.com$/,
              },
            ]}
          >
            <Input placeholder="mystore.myshopify.com" style={{ width: 250 }} />
          </Form.Item>

          <Form.Item
            name="changing_value"
            rules={[
              { required: true, message: 'Value is required' },
              {
                type: 'number',
                min: 1,
                max: 10000,
                message: 'Value must be between 1 and 10000',
              },
            ]}
          >
            <InputNumber placeholder="Amount" min={1} max={10000} />
          </Form.Item>

          <Form.Item name="type" rules={[{ required: true, message: 'Type is required' }]}>
            <Select style={{ width: 120 }}>
              <Option value="plus">Add</Option>
              <Option value="minus">Deduct</Option>
            </Select>
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit">
              Update Credit
            </Button>
          </Form.Item>
        </Form>
      </Card>

      <Card title="Current Credits">
        <div style={{ marginBottom: '16px' }}>
          <Input
            placeholder="Search by shop domain"
            value={filters.credits.myshopify_domain}
            onChange={(e) =>
              setFilters((prev) => ({
                ...prev,
                credits: {
                  ...prev.credits,
                  myshopify_domain: e.target.value,
                },
              }))
            }
            style={{ width: 200, marginRight: '8px' }}
          />
          <Button type="primary" onClick={handleCreditsSearch}>
            Search
          </Button>
        </div>

        <Table
          columns={creditColumns}
          dataSource={credits}
          rowKey="_id"
          pagination={pagination.credits}
          loading={loading.credits}
          onChange={handleCreditsTableChange}
        />
      </Card>

      <Card title="Credit History" style={{ marginTop: '24px' }}>
        <div style={{ marginBottom: '16px' }}>
          <Input
            placeholder="Search by shop domain"
            value={filters.history.myshopify_domain}
            onChange={(e) =>
              setFilters((prev) => ({
                ...prev,
                history: {
                  ...prev.history,
                  myshopify_domain: e.target.value,
                },
              }))
            }
            style={{ width: 200, marginRight: '8px' }}
          />
          <Select
            placeholder="Filter by type"
            value={filters.history.type}
            onChange={(value) =>
              setFilters((prev) => ({
                ...prev,
                history: {
                  ...prev.history,
                  type: value,
                },
              }))
            }
            style={{ width: 120, marginRight: '8px' }}
            allowClear
          >
            <Option value="plus">Add</Option>
            <Option value="minus">Deduct</Option>
          </Select>
          <RangePicker
            onChange={(dates) =>
              setFilters((prev) => ({
                ...prev,
                history: {
                  ...prev.history,
                  dateRange: dates,
                },
              }))
            }
            style={{ marginRight: '8px' }}
          />
          <Button type="primary" onClick={handleHistorySearch}>
            Search
          </Button>
        </div>

        <Table
          columns={historyColumns}
          dataSource={history}
          rowKey="_id"
          pagination={pagination.history}
          loading={loading.history}
          onChange={handleHistoryTableChange}
        />
      </Card>
    </div>
  );
};

export default TapitaCredits;

